<template>
  <button
    class="m-btn"
    :class="btnClass"
    :disabled="disabled || loading"
    :loading="loading"
    :form-type="formType"
    :contact-type="contactType"
    :open-type="openType"
    @tap="onTap"
  >
    <slot />
  </button>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  type: {
    type: String,
    default: 'default',
    validator: (v) => ['default', 'primary', 'info', 'warning', 'danger', 'success'].includes(v)
  },
  size: {
    type: String,
    default: 'default',
    validator: (v) => ['default', 'mini'].includes(v)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  formType: {
    type: String,
    default: ''
  },
  contactType: {
    type: String,
    default: ''
  },
  openType: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['tap']);

const btnClass = computed(() => [
  `m-btn-${props.type}`,
  `m-btn-${props.size}`,
  {
    'm-btn-disabled': props.disabled,
    'm-btn-loading': props.loading
  }
]);

const onTap = (e) => {
  if (!props.disabled && !props.loading) {
    emit('tap', e);
  }
};
</script>

<style scoped>
.m-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
  line-height: 1.5;
}

/* 尺寸 */
.m-btn-default {
  padding: 8px 16px;
  font-size: 14px;
}

.m-btn-mini {
  padding: 4px 10px;
  font-size: 12px;
  border-radius: 6px;
}

/* 默认样式 */
.m-btn-default {
  background: var(--glass-bg);
  border-color: var(--glass-border);
  color: var(--text-primary);
}

.m-btn-default:hover {
  background: var(--card-hover-bg);
  border-color: var(--accent);
}

/* Primary */
.m-btn-primary {
  background: var(--accent);
  color: #fff;
}

.m-btn-primary:hover {
  background: var(--accent-hover);
}

/* Info */
.m-btn-info {
  background: rgba(177, 140, 255, 0.15);
  color: var(--info);
  border-color: rgba(177, 140, 255, 0.3);
}

.m-btn-info:hover {
  background: rgba(177, 140, 255, 0.25);
}

/* Warning */
.m-btn-warning {
  background: rgba(247, 183, 49, 0.15);
  color: var(--warning);
  border-color: rgba(247, 183, 49, 0.3);
}

.m-btn-warning:hover {
  background: rgba(247, 183, 49, 0.25);
}

/* Danger */
.m-btn-danger {
  background: rgba(231, 76, 60, 0.15);
  color: var(--danger);
  border-color: rgba(231, 76, 60, 0.3);
}

.m-btn-danger:hover {
  background: rgba(231, 76, 60, 0.25);
}

/* Success */
.m-btn-success {
  background: rgba(78, 205, 196, 0.15);
  color: var(--success);
  border-color: rgba(78, 205, 196, 0.3);
}

.m-btn-success:hover {
  background: rgba(78, 205, 196, 0.25);
}

/* 禁用状态 */
.m-btn-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* 加载状态 */
.m-btn-loading {
  pointer-events: none;
}

/* 浅色模式 */
.light-mode .m-btn-default {
  background: #fff;
  border-color: rgba(0, 0, 0, 0.15);
  color: #333;
}

.light-mode .m-btn-default:hover {
  border-color: var(--accent);
}

.light-mode .m-btn-primary {
  background: var(--accent);
  color: #fff;
}

.light-mode .m-btn-info {
  background: rgba(111, 66, 193, 0.1);
  color: #6f42c1;
  border-color: rgba(111, 66, 193, 0.25);
}

.light-mode .m-btn-warning {
  background: rgba(247, 183, 49, 0.1);
  color: #b8860b;
  border-color: rgba(247, 183, 49, 0.3);
}

.light-mode .m-btn-danger {
  background: rgba(231, 76, 60, 0.1);
  color: #c0392b;
  border-color: rgba(231, 76, 60, 0.25);
}

.light-mode .m-btn-success {
  background: rgba(78, 205, 196, 0.1);
  color: #17a2b8;
  border-color: rgba(78, 205, 196, 0.3);
}
</style>
